import { useCallback, useEffect, useState } from 'react';

// SVG backgrounds for each spotlight word
import hiddenTalentsSvg from './hidden-talents.svg';
import personalCatchphraseSvg from './personal-catchphrase.svg';
import recommendedReadingSvg from './recommended-reading.svg';
import superpowersSvg from './superpowers.svg';
import topSupportersSvg from './top-supporters.svg';
import topValuesSvg from './top-values.svg';

export interface SpotlightWordConfig {
	/** Translation key for the word */
	labelKey: string;
	/** SVG background URL */
	backgroundSvg: string;
}

/**
 * Configuration for each spotlight section type with its paired SVG background.
 * Order matches the Figma design.
 */
export const SPOTLIGHT_WORDS: SpotlightWordConfig[] = [
	{ labelKey: 'topSupporters', backgroundSvg: topSupportersSvg },
	{ labelKey: 'topValues', backgroundSvg: topValuesSvg },
	{ labelKey: 'superpowers', backgroundSvg: superpowersSvg },
	{ labelKey: 'personalCatchphrase', backgroundSvg: personalCatchphraseSvg },
	{ labelKey: 'hiddenTalents', backgroundSvg: hiddenTalentsSvg },
	{ labelKey: 'recommendedReading', backgroundSvg: recommendedReadingSvg },
];

export interface UseRotatingSpotlightWordOptions {
	/** Interval in milliseconds between word changes. Default: 3000ms */
	interval?: number;
	/** Whether to pause the rotation. Default: false */
	paused?: boolean;
}

export interface UseRotatingSpotlightWordReturn {
	/** Current word configuration */
	current: SpotlightWordConfig;
	/** Index of the current word (0-5) */
	currentIndex: number;
	/** Unique key for each animation cycle (used for SVG cache busting) */
	animationKey: number;
	/** Manually go to next word */
	next: () => void;
	/** Manually go to previous word */
	previous: () => void;
}

/**
 * Hook that cycles through spotlight section types at a regular interval.
 * Returns the current word and its SVG background for animation.
 */
export const useRotatingSpotlightWord = ({ interval = 3000, paused = false }: UseRotatingSpotlightWordOptions = {}): UseRotatingSpotlightWordReturn => {
	const [currentIndex, setCurrentIndex] = useState(0);
	// Unique key that increments on each rotation to bust SVG animation cache
	const [animationKey, setAnimationKey] = useState(0);

	const next = useCallback(() => {
		setCurrentIndex((prev) => (prev + 1) % SPOTLIGHT_WORDS.length);
		setAnimationKey((prev) => prev + 1);
	}, []);

	const previous = useCallback(() => {
		setCurrentIndex((prev) => (prev - 1 + SPOTLIGHT_WORDS.length) % SPOTLIGHT_WORDS.length);
		setAnimationKey((prev) => prev + 1);
	}, []);

	useEffect(() => {
		if (paused) return;

		const timer = setInterval(next, interval);

		return () => clearInterval(timer);
	}, [interval, paused, next]);

	return {
		current: SPOTLIGHT_WORDS[currentIndex],
		currentIndex,
		animationKey,
		next,
		previous,
	};
};
